import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import SubscriptionBanner from './SubscriptionBanner';
import AnnouncementBanner from './AnnouncementBanner';
import VoiceCommandListener from './VoiceCommandListener';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  CreditCard, 
  Settings as SettingsIcon,
  Info,
  Wifi,
  Menu,
  X,
  Moon,
  Sun,
  Sparkles,
  Globe,
  Building2,
  UserCircle,
  LogOut,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Receipt,
  MessageSquare,
  Package,
  Wrench,
  Calculator,
  Server,
  Activity,
  Zap,
  BookOpen,
  HelpCircle,
  Database,
  Mail,
  Shield,
  ShieldAlert,
  Palette,
  Radio,
  Ticket,
  Router,
  MapPin,
  DollarSign,
  Wallet,
  ExternalLink,
  Crown,
  UserCheck,
  Key,
  Bell,
  AlertTriangle,
  Clock,
  Eye,
  Monitor,
  Network,
  Calendar,
  TrendingUp,
  FileEdit,
  HardDrive,
  FileBarChart,
  Trash2,
  History,
  Zap as ZapIcon,
  Leaf,
  Briefcase,
  Sunset as SunsetIcon,
  CircleDot,
  Droplet,
  UsersRound,
  UserRoundCheck,
  Ban,
  CircleDollarSign, Landmark} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import useDashboardTheme from '../hooks/useDashboardTheme';
import { useBranding } from '../contexts/BrandingContext';
import { getContrastText } from '../utils/colorContrast';
import axios from 'axios';
import { getPortalBaseUrl } from '../utils/portal_url';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map menu items to vibrant icon colors (RadiusLink brand-aligned)
const ICON_COLOR_MAP = {
  'dashboard': 'text-blue-400',
  'super admin': 'text-amber-400',
  'tenant billing': 'text-emerald-400',
  'subscription plans': 'text-pink-400',
  'l2tp management': 'text-cyan-400',
  'gateway settings': 'text-orange-400',
  'admin tools': 'text-red-400',
  'system settings': 'text-slate-400',
  'session management': 'text-purple-400',
  'branch management': 'text-rose-400',
  'company info': 'text-teal-400',
  'vpn management': 'text-indigo-400',
  'status router': 'text-emerald-400',
  'sistem info': 'text-cyan-400',
  'automation': 'text-yellow-400',
  'isolir management': 'text-red-400',
  'editor isolir global': 'text-rose-400',
  'backup manager': 'text-blue-400',
  'phpmyadmin': 'text-orange-400',
  'reminder & pengumuman': 'text-yellow-400',
  'data odp/pop': 'text-cyan-400',
  'perpanjangan tenant': 'text-purple-400',
  'acs tools': 'text-pink-400',
  'pelanggan': 'text-blue-400',
  'portal pelanggan': 'text-emerald-400',
  'paket layanan': 'text-orange-400',
  'ip pool': 'text-violet-400',
  'voucher hotspot': 'text-amber-400',
  'cabang': 'text-rose-400',
  'pemilik/owner': 'text-indigo-400',
  'deposit owner': 'text-emerald-400',
  'akuntansi': 'text-teal-400',
  'monitoring': 'text-pink-400',
  'mpanel': 'text-purple-400',
  'pengaturan perusahaan': 'text-orange-400',
  'users': 'text-blue-400',
  'roles & permissions': 'text-yellow-400',
  'tickets': 'text-red-400',
  'tools sistem': 'text-cyan-400',
  'panduan': 'text-emerald-400',
  'pengaturan': 'text-slate-400',
};

const FALLBACK_PALETTE = ['text-blue-400', 'text-orange-400', 'text-emerald-400', 'text-purple-400', 'text-pink-400', 'text-cyan-400', 'text-yellow-400', 'text-rose-400', 'text-indigo-400', 'text-teal-400'];

const getIconColor = (_label) => {
  // Unified white tone for all sidebar icons
  return 'text-white';
};

// 2026-02-04: Komponen LOGO Radiuslink — pill putih dengan shadow tegas supaya visible di semua tema.
const BrandLogo = () => {
  return (
    <a
      href="https://radiuslink.id"
      target="_blank"
      rel="noopener noreferrer"
      className="hidden md:inline-flex items-center group"
      title="RadiusLink — ISP Billing Enterprise"
      data-testid="brand-radiuslink"
    >
      <div
        className="inline-flex items-center h-8 sm:h-9 px-3 rounded-lg bg-white group-hover:bg-white ring-1 ring-slate-300/80 transition-all group-hover:ring-slate-400"
        style={{
          boxShadow: '0 4px 12px -2px rgba(15, 23, 42, 0.18), 0 2px 4px -1px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(255,255,255,0.6) inset'
        }}
      >
        <img
          src="/radiuslink-topbar.png"
          alt="RadiusLink"
          className="h-6 sm:h-7 w-auto object-contain"
          draggable="false"
        />
      </div>
    </a>
  );
};

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, toggleTheme, theme, toggleLanguage, language, selectedBranch, setSelectedBranch, selectedRouter, setSelectedRouter } = useApp();
  const dashboardTheme = useDashboardTheme();
  const { user, logout, hasPermission, hasSpecificPermission } = useAuth();
  const { branding, refreshBranding, currentTheme: brandingCurrentTheme } = useBranding();
  // Active-menu gradient: ALWAYS honor user's primary/secondary color.
  // Text color auto-adapts to contrast against primary so text never disappears.
  const _primaryHex = branding?.primary_color || '#4F46E5';
  const _secondaryHex = branding?.secondary_color || '#10B981';
  const activeMenuBg = `linear-gradient(135deg, ${_primaryHex} 0%, ${_secondaryHex} 100%)`;
  const activeMenuText = getContrastText(_primaryHex);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [routers, setRouters] = useState([]);
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [customersOpen, setCustomersOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [monitoringOpen, setMonitoringOpen] = useState(false);
  const [acsToolsOpen, setAcsToolsOpen] = useState(false);
  const [aktivitasLogOpen, setAktivitasLogOpen] = useState(true);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [ownersOpen, setOwnersOpen] = useState(false);
  const [companySettingsOpen, setCompanySettingsOpen] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState(0);
  const [odpOpen, setOdpOpen] = useState(false);
  const [tenantCode, setTenantCode] = useState('');
  const [portalOpen, setPortalOpen] = useState(false);
  const [tenantsList, setTenantsList] = useState([]);
  
  // Overdue tickets notification state
  const [overdueTickets, setOverdueTickets] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Define functions BEFORE useEffects that use them
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // Fetch routers — used by the global Router selector in the header.
  // IMPORTANT: use /api/v2/mpanel/available-routers — it returns the numeric
  // mikrotiks.id which matches customers.router_id (INT). The older /api/mikrotiks
  // endpoint exposes UUID as `id` which breaks the dashboard router-filter.
  const fetchRouters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/mpanel/available-routers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : (payload?.data || payload?.routers || []);
      setRouters(list);
    } catch (error) {
      console.error('Error fetching routers:', error);
    }
  };

  const fetchAllTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants?per_page=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data) {
        // Filter only active tenants with tenant_code
        const activeTenants = response.data.data.filter(t => t.tenant_code && t.subscription_status === 'active');
        setTenantsList(activeTenants);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchTenantCode = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // For super_admin, always get first tenant from list
      if (user?.role === 'super_admin') {
        const response = await axios.get(`${API_URL}/api/tenants?per_page=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success && response.data.data?.length > 0) {
          const code = response.data.data[0].tenant_code;
          console.log('SuperAdmin - Tenant code fetched:', code);
          setTenantCode(code);
        }
        return;
      }
      
      // For regular admin, get their tenant
      const response = await axios.get(`${API_URL}/api/tenant/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data?.tenant?.tenant_code) {
        const code = response.data.data.tenant.tenant_code;
        console.log('Admin - Tenant code fetched:', code);
        setTenantCode(code);
      }
    } catch (error) {
      console.error('Error fetching tenant code:', error);
    }
  };

  // Fetch overdue tickets for notification
  const fetchOverdueTickets = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Only fetch for admin roles
      if (!hasPermission(['admin', 'kepala_cabang', 'customer_service', 'super_admin'])) return;
      
      const response = await axios.get(`${API_URL}/api/tickets/overdue?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        setOverdueTickets(response.data.data.tickets || []);
        setOverdueCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching overdue tickets:', error);
    }
  }, [hasPermission]);

  const openTenantPortal = (tenantCode) => {
    window.open(`${getPortalBaseUrl()}/p/${tenantCode}/login`, '_blank');
  };

  const openCustomerPortal = async () => {
    const portalBaseUrl = getPortalBaseUrl();
    
    // If tenant code already available, open directly
    if (tenantCode) {
      window.open(`${portalBaseUrl}/p/${tenantCode}/login`, '_blank');
      return;
    }
    
    // Try to fetch tenant code on-the-fly
    try {
      const token = localStorage.getItem('token');
      let code = null;
      
      if (user?.role === 'super_admin') {
        const response = await axios.get(`${API_URL}/api/tenants?per_page=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success && response.data.data?.length > 0) {
          code = response.data.data[0].tenant_code;
        }
      } else {
        const response = await axios.get(`${API_URL}/api/tenant/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success && response.data.data?.tenant?.tenant_code) {
          code = response.data.data.tenant.tenant_code;
        }
      }
      
      if (code) {
        setTenantCode(code);
        window.open(`${portalBaseUrl}/p/${code}/login`, '_blank');
      } else {
        alert('Tenant tidak ditemukan. Pastikan ada tenant yang terdaftar di sistem.');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      alert('Gagal membuka portal pelanggan. Silakan coba lagi.');
    }
  };

  const handleLogout = () => {
    // Short neutral pulse on mobile — confirms the logout gesture before
    // the splash overlay takes over. Safe no-op on browsers without
    // navigator.vibrate support.
    try {
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(30);
    } catch (_e) { /* noop */ }
    // Show splash overlay (premium "perceived speed" fade) BEFORE reload.
    // Full page navigation guarantees the next /login render starts from a
    // completely clean slate (clears stale CSS variables, dark/light class
    // mutations from AppContext, animation states, and backdrop-filter
    // compositing artifacts that linger on some mobile browsers and would
    // otherwise require a manual refresh).
    setLoggingOut(true);
    setTimeout(() => {
      try { logout(); } catch (_e) {}
      window.location.replace('/login');
    }, 480);
  };

  // useEffects - placed AFTER all function definitions

  // Refresh branding when user changes (after login) - dedup'd internally in BrandingContext
  // (event-driven via 'userLoggedIn' is the primary path; this is a safety net)
  useEffect(() => {
    if (user) {
      refreshBranding();
    }
  }, [user?.id]);

  useEffect(() => {
    fetchBranches();
    fetchRouters();
    
    // Auto-expand menu if on related page
    if (location.pathname.includes('/vouchers') || location.pathname.includes('/hotspot-packages')) {
      setVoucherOpen(true);
    }
    if (location.pathname.includes('/company-profile') || location.pathname.includes('/tenant-vpn')) {
      setCompanySettingsOpen(true);
    }
    if (location.pathname.includes('/customers')) {
      setCustomersOpen(true);
    }
    if (location.pathname.includes('/acs-') || location.pathname.includes('/ont-') || location.pathname.includes('/wan-')) {
      setAcsToolsOpen(true);
    }
    if (location.pathname.includes('/owners') || location.pathname.includes('/owner-statistics')) {
      setOwnersOpen(true);
    }
    if (location.pathname.includes("/odp-") || location.pathname.includes("/customer-map")) { setOdpOpen(true); }
    if (location.pathname.includes("/audit-log") || location.pathname.includes("/trash")) { setAktivitasLogOpen(true); }
  }, [location.pathname]);

  // Fetch tenant code and tenants list when user is available
  useEffect(() => {
    if (user) {
      fetchTenantCode();
      if (user.role === 'super_admin') {
        fetchAllTenants();
      }
    }
  }, [user]);

  // Fetch overdue tickets and auto-refresh every 5 minutes
  useEffect(() => {
    if (user && hasPermission(['admin', 'kepala_cabang', 'customer_service', 'super_admin'])) {
      fetchOverdueTickets();
      
      // Auto-refresh every 5 minutes
      const interval = setInterval(() => {
        fetchOverdueTickets();
      }, 5 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [user, fetchOverdueTickets, hasPermission]);

  // Fetch pending registrations count for sidebar badge (auto-refresh every 60s)
  useEffect(() => {
    const canView = user && hasPermission(['admin', 'kepala_cabang', 'customer_service', 'owner', 'super_admin']);
    if (!canView) return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const resp = await fetch(`${API_URL}/api/v2/registrations?status=new&limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) setPendingRegistrations(Number(data?.stats?.new || 0));
      } catch (_e) { /* silent */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, hasPermission]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationOpen && !event.target.closest('[data-notification-dropdown]')) {
        setNotificationOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationOpen]);


  // Update browser tab title based on current route + active branch filter
  useEffect(() => {
    // Build page name from pathname
    const path = location.pathname;
    const titleMap = {
      '/app/dashboard': 'Dashboard',
      '/app/customers': 'Pelanggan',
      '/app/invoices': 'Tagihan',
      '/app/invoices/generate': 'Generate Tagihan',
      '/app/payments': 'Pembayaran',
      '/app/expenses': 'Pengeluaran',
      '/app/service-plans': 'Paket Layanan',
      '/app/owner-deposits': 'Deposit Owner',
      '/app/owners': 'Daftar Owner',
      '/app/owner-statistics': 'Statistik Owner',
      '/app/odp-management': 'Data ODP/POP',
      '/app/ip-allocations': 'Alokasi IP Pelanggan',
      '/app/odp-map': 'Peta ODP/POP',
      '/app/customer-map': 'Peta Pelanggan & ODP',
      '/app/vouchers': 'Voucher Hotspot',
      '/app/hotspot-online': 'Hotspot Online',
      '/app/mikrotik-monitoring': 'Monitoring Mikrotik',
      '/app/isolir-management': 'Isolir',
      '/app/subnet-planner': 'Distribusi IP',
      '/app/tickets': 'Tickets',
      '/app/laporan-bulanan': 'Laporan Bulanan',
      '/app/trash': 'Arsip Terhapus',
      '/app/audit-logs': 'Audit Log',
      '/app/email-logs': 'Riwayat Notifikasi',
      '/app/reconciliation': 'Rekonsiliasi Bank',
      '/app/revenue': 'Pendapatan',
    };
    let pageName = titleMap[path];
    if (!pageName) {
      const match = Object.keys(titleMap).find(p => path.startsWith(p));
      pageName = match ? titleMap[match] : 'Dashboard';
    }
    const appName = branding?.app_name || 'RADIUSLINK';
    const isBranchActive = selectedBranch && selectedBranch !== 'all';
    const activeBranchName = branches.find(b => String(b.id) === String(selectedBranch))?.name;
    if (isBranchActive && activeBranchName) {
      document.title = `[${activeBranchName}] ${pageName} — ${appName}`;
    } else {
      document.title = `${pageName} — ${appName}`;
    }
  }, [location.pathname, selectedBranch, branches, branding?.app_name]);

  const navItems = [
    // ============ DASHBOARD ============
    { path: '/app/agent-dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['voucher_agent'] },
    { path: '/app/dashboard', label: t('dashboard'), icon: LayoutDashboard, roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi'], permissions: ['dashboard.view'], excludeRoles: ['super_admin', 'superadmin'] },
    { path: '/app/superadmin-home', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin'], excludeRoles: ['admin'] },
    
    // ============ SUPER ADMIN ONLY ============
    { path: '/app/super-admin', label: 'Super Admin', icon: Crown, roles: ['super_admin'] },
    { path: '/app/tenant-billing', label: 'Tenant Billing', icon: CreditCard, roles: ['super_admin'] },
    { path: '/app/subscription-plans', label: 'Subscription Plans', icon: Package, roles: ['super_admin'] },
    { path: '/app/l2tp-management', label: 'L2TP Management', icon: Wifi, roles: ['super_admin'] },
    { path: '/app/gateway-permissions', label: 'Gateway Settings', icon: SettingsIcon, roles: ['super_admin'] },
    { path: '/app/superadmin-tools', label: 'Admin Tools', icon: Wrench, roles: ['super_admin'] },
    { path: '/app/system-settings', label: 'System Settings', icon: SettingsIcon, roles: ['super_admin'] },
    { path: '/app/session-management', label: 'Session Management', icon: Monitor, roles: ['super_admin'] },
    { path: '/app/branch-management', label: 'Branch Management', icon: MapPin, roles: ['super_admin'] },
    { path: '/app/company-info', label: 'Company Info', icon: Building2, roles: ['super_admin'] },
    { path: '/app/superadmin-vpn-management', label: 'VPN Management', icon: Shield, roles: ['super_admin'] },
    { path: '/app/superadmin-router-status', label: 'Status Router', icon: Router, roles: ['super_admin'] },
    { path: '/app/system-info', label: 'Sistem Info', icon: Info, roles: ['super_admin'] },
    { path: '/app/automation', label: 'Automation', icon: Zap, roles: ['super_admin'] },
    { path: '/app/isolir-management', label: 'Isolir Management', icon: Ban, roles: ['super_admin', 'admin', 'tenant_admin'] },
    { path: '/app/isolir-global-editor', label: 'Editor Isolir Global', icon: FileEdit, roles: ['super_admin'] },
    { path: '/app/backup-manager', label: 'Backup Manager', icon: HardDrive, roles: ['super_admin'] },
    { label: 'phpMyAdmin', icon: Database, roles: ['super_admin'], isExternal: true, externalUrl: '/phpmyadmin/' },
    { path: '/app/reminder-settings', label: 'Reminder & Pengumuman', icon: Bell, roles: ['super_admin'] },
    { path: '/app/laporan-bulanan', label: 'Laporan Bulanan', icon: FileBarChart, roles: ['super_admin'] },
    { path: "/odp", label: "Data ODP/POP", icon: Network, roles: ["admin", "kepala_cabang", "owner", "teknisi", "super_admin"], submenu: [{ path: "/app/odp-management", label: "Kelola ODP/POP", roles: ["admin", "kepala_cabang", "owner", "teknisi", "super_admin"] }, { path: "/app/odp-map", label: "Peta ODP/POP", roles: ["admin", "kepala_cabang", "owner", "teknisi", "super_admin"] }, { path: "/app/customer-map", label: "Peta Pelanggan & ODP", roles: ["admin", "kepala_cabang", "owner", "teknisi", "super_admin"] }] },
    { path: '/app/tenant-subscription', label: 'Perpanjangan Tenant', icon: Calendar, roles: ['super_admin'] },
    { 
      label: 'ACS Tools', 
      icon: Wrench,
      roles: ['super_admin'],
      submenu: [
        { path: '/app/ont-provisioning', label: 'ONT Provisioning', roles: ['super_admin'] },
        { path: '/app/wan-devices', label: 'WAN Devices', roles: ['super_admin'] },
        { path: '/app/wan-overview', label: 'WAN Overview', roles: ['super_admin'] },
      ]
    },

    // ============ PELANGGAN ============
    { 
      path: '/app/customers', 
      label: t('customers'), 
      icon: UsersRound, 
      roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi'],
      permissions: ['customers.view'],
      excludeRoles: ['super_admin'],
      submenu: [
        { path: '/app/customers', label: 'Semua Pelanggan', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'teknisi'], permissions: ['customers.view'] },
        { path: '/app/customers/pppoe', label: 'PPPoE', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'teknisi'], permissions: ['customers.view'] },
        { path: '/app/customers/hotspot', label: 'Hotspot', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'teknisi'], permissions: ['customers.view'] },
        { path: '/app/customers/dhcp', label: 'Statik/DHCP', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'teknisi'], permissions: ['customers.view'] },
        { path: '/app/customers/ip-conflicts', label: 'IP Conflict', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'teknisi'], permissions: ['customers.view'] },
        { path: '/app/device-inventory', label: 'Inventaris Perangkat', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'teknisi'], permissions: ['customers.view'] },
        { path: '/app/registrations', label: 'Pendaftaran Baru', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'], permissions: ['customers.view'] },
      ]
    },
    { 
      label: 'Portal Pelanggan', 
      icon: Globe, 
      roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'],
      excludeRoles: ['super_admin'],
      isExternal: true,
      onClick: 'openCustomerPortal'
    },
    { path: '/app/service-plans', label: t('servicePlans'), icon: Package, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'], excludeRoles: ['super_admin'] },
    { path: '/app/ip-pools', label: 'IP Pool', icon: Network, roles: ['admin', 'kepala_cabang', 'owner'], excludeRoles: ['super_admin'] },
    { path: '/app/ip-allocations', label: 'Alokasi IP Pelanggan', icon: Network, roles: ['admin', 'kepala_cabang', 'owner', 'super_admin'] },
    {
      path: '/voucher',
      label: 'Voucher Hotspot',
      icon: Ticket,
      roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'voucher_agent'],
      excludeRoles: ['super_admin'],
      submenu: [
        { path: '/app/hotspot-packages', label: 'Paket Hotspot', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'] },
        { path: '/app/vouchers', label: 'Kelola Voucher', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'voucher_agent'] },
        { path: '/app/manajemen-voucher', label: 'Manajemen Voucher', roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'] },
        { path: '/app/voucher-template-editor', label: 'Editor Template Voucher', roles: ['admin', 'owner'] },
        { path: '/app/voucher-agents', label: 'Agen Voucher', roles: ['admin', 'kepala_cabang'] },
        { path: '/app/voucher-migration', label: 'Migrasi Voucher', roles: ['admin'] },
      ]
    },

    // ============ MANAJEMEN ============
    { path: '/app/branches', label: t('branches'), icon: Building2, roles: ['admin', 'kepala_cabang', 'customer_service'], excludeRoles: ['super_admin'] },
    { 
      path: '/owners-menu',
      label: t('owners'), 
      icon: UserRoundCheck, 
      roles: ['admin', 'kepala_cabang', 'customer_service'],
      excludeRoles: ['super_admin'],
      submenu: [
        { path: '/app/owners', label: 'Daftar Owner', roles: ['admin', 'kepala_cabang', 'customer_service'] },
        { path: '/app/owner-statistics', label: 'Statistik Owner', roles: ['admin', 'kepala_cabang', 'customer_service'] },
        { path: '/app/users', label: 'Users / Pegawai', roles: ['admin', 'kepala_cabang'] },
      ]
    },
    { path: '/app/owner-deposits', label: 'Deposit Owner', icon: DollarSign, roles: ['admin', 'kepala_cabang', 'customer_service'], excludeRoles: ['super_admin'] },
    { path: '/app/my-deposits', label: 'Saldo Saya', icon: Wallet, roles: ['owner'] },

    // ============ AKUNTANSI (Placeholder) ============
    { isAkuntansiPlaceholder: true, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'], excludeRoles: ['super_admin'], permissions: ['invoices.view', 'billing.view', 'reports.view'] },

    // ============ MONITORING ============
    { 
      path: '/monitoring',
      label: 'Monitoring', 
      icon: Radio, 
      roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi', 'owner'],
      excludeRoles: ['super_admin'],
      permissions: ['monitoring.view'],
      submenu: [
        { path: '/app/live-sessions', label: 'Live Sessions', roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi', 'owner', 'super_admin'], permissions: ['monitoring.view'] },
        { path: '/app/pppoe-online', label: 'PPPoE Monitoring', roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi', 'owner'], permissions: ['monitoring.view'] },
        { path: '/app/hotspot-aktif', label: 'Hotspot Aktif', roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi', 'owner'], permissions: ['monitoring.view'] },
        { path: '/app/acs-monitoring', label: 'ACS Monitoring', roles: ['admin', 'kepala_cabang', 'customer_service', 'teknisi', 'owner'], permissions: ['monitoring.view'] },
        { path: '/app/olt', label: 'OLT', roles: ['admin', 'kepala_cabang', 'teknisi', 'owner'], permissions: ['monitoring.view'] },
      ]
    },
    { path: '/app/mpanel', label: 'MPanel', icon: Router, roles: ['admin', 'kepala_cabang'], excludeRoles: ['super_admin'], permissions: ['monitoring.view'] },
    { path: '/app/subnet-planner', label: 'Distribusi IP', icon: Network, roles: ['admin', 'super_admin'], permissions: ['monitoring.view'] },

    // ============ PENGATURAN ============
    { 
      path: '/app/company-settings',
      label: 'Pengaturan Perusahaan', 
      icon: Building2, 
      roles: ['admin'],
      excludeRoles: ['super_admin'],
      submenu: [
        { path: '/app/company-profile', label: 'Profil Perusahaan', roles: ['admin'] },
        { path: '/app/tenant-vpn', label: 'Kelola VPN', roles: ['admin'] },
      ]
    },
    { path: '/app/roles', label: 'Roles & Permissions', icon: Key, roles: ['admin', 'super_admin'] },
    { path: '/app/role-matrix', label: 'Role Matrix', icon: Shield, roles: ['admin', 'super_admin', 'owner'] },

    // ============ LAINNYA ============
    { path: '/app/tickets', label: 'Tickets', icon: Ticket, roles: ['admin', 'kepala_cabang', 'teknisi'], excludeRoles: ['super_admin'] },
    { path: '/app/system-tools', label: t('systemTools'), icon: Wrench, roles: ['admin'], excludeRoles: ['super_admin'] },
    { path: '/app/migrasi-data', label: 'Migrasi Data', icon: Wrench, roles: ['admin'], excludeRoles: ['super_admin'] },
    { path: '/app/help', label: 'Panduan', icon: BookOpen, roles: ['admin', 'kepala_cabang'], excludeRoles: ['super_admin'] },
  ].filter(item => {
    const userRole = user?.role?.toLowerCase();
    
    // First check excludeRoles - if user role is excluded, don't show
    if (item.excludeRoles && item.excludeRoles.includes(userRole)) {
      return false;
    }
    
    // For super_admin, only show if super_admin is explicitly in roles
    if (['super_admin', 'superadmin'].includes(userRole)) {
      return item.roles && item.roles.includes('super_admin');
    }
    
    // For other roles, use standard hasPermission
    return hasPermission(item.roles, item.permissions);
  });

  const accountingItems = [
    { path: '/app/invoices', label: t('invoices'), icon: FileText, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'], permissions: ['invoices.view', 'billing.view'] },
    { path: '/app/invoices/generate', label: 'Generate Tagihan', icon: FileText, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'super_admin'], permissions: ['invoices.create', 'billing.view'] },
    { path: '/app/payments', label: t('payments'), icon: CreditCard, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'], permissions: ['payments.view', 'billing.view'] },
    { path: '/app/online-payment-report', label: 'Online Payment', icon: CreditCard, roles: ['admin', 'super_admin', 'kepala_cabang', 'owner'] },
    { path: '/app/voucher-income', label: 'Pendapatan Voucher', icon: Ticket, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'] },
    { path: '/app/expenses', label: t('expenses'), icon: Receipt, roles: ['admin', 'kepala_cabang', 'customer_service'] },
    { path: '/app/revenue', label: 'Pendapatan', icon: TrendingUp, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner'] },
    { path: '/app/inventory', label: 'Inventaris', icon: Package, roles: ['admin', 'owner', 'tenant_admin', 'kepala_cabang'] },
    { path: '/app/accounting', label: 'Akuntansi', icon: Calculator, roles: ['admin'] },
    { path: '/app/whatsapp-cost', label: 'Biaya WhatsApp', icon: ({ className }) => (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    ), roles: ['admin', 'kepala_cabang', 'customer_service', 'super_admin', 'owner'] },
    { path: '/app/reports', label: 'Laporan Keuangan', icon: BarChart3, roles: ['admin', 'kepala_cabang', 'customer_service'], permissions: ['reports.view'] },
    { path: '/app/receivables', label: 'Laporan Piutang', icon: CircleDollarSign, roles: ['admin', 'kepala_cabang', 'super_admin', 'owner'] },
    { path: '/app/laporan-bulanan', label: 'Laporan Bulanan', icon: FileBarChart, roles: ['admin', 'kepala_cabang', 'customer_service', 'owner', 'super_admin'], permissions: ['reports.view'] },
    { path: '/app/bhp-uso-report', label: 'Laporan BHP/USO', icon: Calculator, roles: ['admin', 'kepala_cabang', 'owner'] },
  ].filter(item => hasPermission(item.roles, item.permissions));

  const settingsItems = [
    { path: '/app/settings', label: 'Pengaturan Sistem', icon: SettingsIcon, roles: ['admin'] },
    { path: '/app/system-monitor', label: 'System Monitor', icon: Radio, roles: ['admin', 'super_admin'] },
    { path: '/app/admin-tools', label: 'MikroTik Tools', icon: Router, roles: ['super_admin'] },
    { path: '/app/olt', label: 'OLT Devices', icon: Router, roles: ['admin'] },
    { path: '/app/payment-gateway', label: 'Payment Gateway', icon: CreditCard, roles: ['admin', 'super_admin'] },
    { path: '/app/whatsapp', label: 'WhatsApp', icon: MessageSquare, roles: ['admin', 'kepala_cabang'] },
    { path: '/app/email-smtp', label: 'Email / SMTP', icon: Mail, roles: ['admin'] },
    { path: '/app/mikrotik', label: 'Konfigurasi MikroTik', icon: Router, roles: ['admin', 'kepala_cabang', 'super_admin'] },
    { path: '/app/isolir-page-editor', label: 'Halaman Isolir', icon: Router, roles: ['admin', 'super_admin'] },
    { path: '/app/radius-management', label: 'RADIUS Management', icon: Radio, roles: ['super_admin'] }, // RADIUS only for superadmin
    { path: '/app/hotspot-customizer', label: 'Hotspot Login', icon: Palette, roles: ['admin'] },
    { path: '/app/app-version', label: 'Versi Aplikasi', icon: Info, roles: ['admin'] },
  ].filter(item => hasPermission(item.roles));

  // Aktivitas / Log group — placed after Pengaturan
  const aktivitasLogItems = [
    { path: '/app/audit-logs', label: 'Audit Log', icon: History, roles: ['super_admin', 'admin', 'kepala_cabang'] },
    { path: '/app/email-logs', label: 'Riwayat Notifikasi', icon: Mail, roles: ['super_admin', 'admin', 'kepala_cabang'] },
    { path: '/app/reconciliation', label: 'Rekonsiliasi Bank', icon: Landmark, roles: ['super_admin', 'admin'] },
    { path: '/app/trash', label: 'Arsip Terhapus', icon: Trash2, roles: ['super_admin', 'admin', 'kepala_cabang'] },
  ].filter(item => hasPermission(item.roles));

  // Themes that have CSS overlay (dashboard-themes.css) defining sidebar/header/body.
  // For these themes, inline background should be applied ONLY when user explicitly
  // saved a per-theme override — otherwise let the bawaan CSS overlay show through.
  const THEMES_WITH_OVERLAY = []; // 2026-07-11 removed exotic themes; only glass/light/dark remain
  const _getCurrentDashTheme = () => {
    // Prefer the theme exposed by BrandingContext (kept in sync via 'rl-theme-change' event detail)
    // to avoid race with useDashboardTheme's localStorage useEffect that commits after render.
    if (brandingCurrentTheme) return brandingCurrentTheme;
    try { return localStorage.getItem('rl_dashboard_theme') || 'glass'; } catch { return 'glass'; }
  };
  // Returns true if user has explicitly saved one of `fields` in theme_overrides[currentTheme]
  // OR the current theme is not an overlay theme (Glass/Light/Dark/Paper-White always custom).
  const _hasUserOverride = (fields) => {
    const t = _getCurrentDashTheme();
    if (!THEMES_WITH_OVERLAY.includes(t)) return true;
    const ov = branding.theme_overrides && branding.theme_overrides[t];
    if (!ov) return false;
    return fields.some((f) => ov[f] !== undefined && ov[f] !== null && ov[f] !== '');
  };

  // Calculate sidebar background style + sidebar text color via CSS var
  const getSidebarStyle = () => {
    const base = branding.sidebar_text_color
      ? { '--sidebar-text-color': branding.sidebar_text_color }
      : {};
    // For overlay themes without explicit sidebar override, omit background so CSS overlay applies
    if (!_hasUserOverride(['sidebar_color', 'sidebar_gradient_start', 'sidebar_gradient_end', 'sidebar_use_gradient'])) {
      return base;
    }
    if (branding.sidebar_use_gradient) {
      return {
        ...base,
        background: `linear-gradient(180deg, ${branding.sidebar_gradient_start || '#000000'} 0%, ${branding.sidebar_gradient_end || '#1a1a1a'} 100%)`
      };
    }
    return {
      ...base,
      backgroundColor: branding.sidebar_color || '#000000'
    };
  };

  // Calculate content/dashboard background style + text color
  const getContentBgStyle = () => {
    const baseTxt = branding.primary_text_color ? { color: branding.primary_text_color } : {};
    if (!_hasUserOverride(['content_bg_color', 'content_bg_gradient_start', 'content_bg_gradient_end', 'content_use_gradient'])) {
      return baseTxt;
    }
    if (branding.content_use_gradient) {
      return {
        ...baseTxt,
        background: `linear-gradient(135deg, ${branding.content_bg_gradient_start || '#f8fafc'} 0%, ${branding.content_bg_gradient_end || '#e2e8f0'} 100%)`
      };
    }
    return {
      ...baseTxt,
      backgroundColor: branding.content_bg_color || '#f8fafc'
    };
  };

  // Calculate header/navbar style
  const getHeaderStyle = () => {
    if (!_hasUserOverride(['header_color', 'header_gradient_start', 'header_gradient_end', 'header_use_gradient'])) {
      return {};
    }
    if (branding.header_use_gradient) {
      return {
        background: `linear-gradient(90deg, ${branding.header_gradient_start || '#3b82f6'} 0%, ${branding.header_gradient_end || '#0ea5e9'} 100%)`
      };
    }
    return {
      backgroundColor: branding.header_color || '#2563eb'
    };
  };

  return (
    <>
      {loggingOut && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center logout-splash"
          style={{
            background: 'radial-gradient(circle at 50% 50%, #0f172a 0%, #020617 70%, #000 100%)',
          }}
          data-testid="logout-splash"
        >
          <div className="logout-splash-inner flex flex-col items-center gap-5">
            <div className="relative w-14 h-14">
              <div className="absolute inset-0 rounded-full border-2 border-white/10" />
              <div className="absolute inset-0 rounded-full border-2 border-t-white/70 border-r-white/30 border-b-transparent border-l-transparent animate-spin" />
            </div>
            <div className="text-white/90 text-sm font-medium tracking-wide">Sedang keluar...</div>
            <div className="text-white/40 text-xs">Membersihkan sesi & menyiapkan halaman login</div>
          </div>
          <style>{`
            @keyframes logoutSplashFade {
              0%   { opacity: 0; backdrop-filter: blur(0px); }
              100% { opacity: 1; backdrop-filter: blur(8px); }
            }
            @keyframes logoutInnerRise {
              0%   { opacity: 0; transform: translateY(8px) scale(0.98); }
              100% { opacity: 1; transform: translateY(0) scale(1); }
            }
            .logout-splash { animation: logoutSplashFade 0.32s ease-out forwards; }
            .logout-splash-inner { animation: logoutInnerRise 0.36s 0.05s cubic-bezier(0.16, 1, 0.3, 1) both; }
          `}</style>
        </div>
      )}
    <div className="flex h-screen" style={getContentBgStyle()}>
      {/* Sidebar */}
      <aside 
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 shadow-xl transition-transform duration-300 ease-in-out border-r border-[color-mix(in_srgb,currentColor_12%,transparent)]`}
        style={getSidebarStyle()}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center justify-center px-3 py-1.5 relative" style={{ borderBottom: '1px solid color-mix(in srgb, currentColor 10%, transparent)' }}>
            {branding.logo_url ? (
              // Tenant with White Label: Custom logo - centered and scale adjustable
              <div className="h-12 flex items-center justify-center overflow-visible">
                <img 
                  src={branding.logo_url} 
                  alt={branding.company_name} 
                  className="max-h-10 w-auto object-contain"
                  style={{ 
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                    transform: `scale(${branding.logo_scale || 1.0})`,
                    transformOrigin: 'center center'
                  }}
                />
              </div>
            ) : (
              // No White Label (Super Admin or Tenant): RADIUSLINK logo
              <img 
                src="/radiuslink-logo.png" 
                alt="RADIUSLINK" 
                className="h-8 w-auto object-contain"
              />
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 min-h-0 p-2 space-y-0.5 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {/* All menu items in single loop - Akuntansi rendered at placeholder position */}
            {navItems.map((item) => {
              // Check excludeRoles first
              const userRole = user?.role?.toLowerCase();
              if (item.excludeRoles && item.excludeRoles.includes(userRole)) {
                return null;
              }
              
              // Render Akuntansi group at placeholder position
              if (item.isAkuntansiPlaceholder) {
                // Check permission dynamically
                if (!hasPermission(item.roles, item.permissions)) return null;
                // Also check if there are any visible accounting items
                if (accountingItems.length === 0) return null;
                return (
                  <div key="akuntansi-group">
                    <button
                      onClick={() => setAccountingOpen(!accountingOpen)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                        accountingItems.some(ai => location.pathname === ai.path)
                          ? 'text-white shadow-lg'
                          : 'hover:bg-black/10 dark:hover:bg-white/10'
                      }`}
                      style={accountingItems.some(ai => location.pathname === ai.path) ? {
                        background: `linear-gradient(to right, ${branding.primary_color || '#4F46E5'}, ${branding.secondary_color || '#10B981'})`
                      } : {}}
                    >
                      <div className="flex items-center space-x-2">
                        <Calculator className="w-4 h-4" />
                        <span className="text-xs font-medium">Laporan</span>
                      </div>
                      <ChevronDown className={`w-3 h-3 transition-transform ${accountingOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {accountingOpen && (
                      <div className="ml-4 mt-1 space-y-0.5">
                        {accountingItems.filter(ai => hasPermission(ai.roles)).map((ai) => {
                          const AIcon = ai.icon;
                          return (
                            <Link key={ai.path} to={ai.path}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                                location.pathname === ai.path
                                  ? 'text-white'
                                  : 'hover:bg-black/10 dark:hover:bg-white/10'
                              }`}
                              style={location.pathname === ai.path ? {
                                background: activeMenuBg,
                                color: activeMenuText,
                                opacity: 0.95
                              } : {}}
                            >
                              <AIcon className="w-3 h-3" />
                              <span>{ai.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              // Handle menu with submenu (dynamic)
              if (item.submenu) {
                const isAnySubmenuActive = item.submenu.some(sub => location.pathname === sub.path);
                
                // Determine which state to use based on menu path/label
                const isOpen = item.path === '/odp' ? odpOpen :
                               item.path === '/app/customers' ? customersOpen :
                               item.path === '/monitoring' ? monitoringOpen :
                               item.path === '/voucher' ? voucherOpen :
                               item.path === '/owners-menu' ? ownersOpen :
                               item.path === '/app/company-settings' ? companySettingsOpen :
                               item.label === 'ACS Tools' ? acsToolsOpen :
                               item.label === 'Aktivitas / Log' ? aktivitasLogOpen : false;
                               
                const toggleOpen = item.path === '/odp' ? setOdpOpen :
                                   item.path === '/app/customers' ? setCustomersOpen :
                                   item.path === '/monitoring' ? setMonitoringOpen :
                                   item.path === '/voucher' ? setVoucherOpen :
                                   item.path === '/owners-menu' ? setOwnersOpen :
                                   item.path === '/app/company-settings' ? setCompanySettingsOpen :
                                   item.label === 'ACS Tools' ? setAcsToolsOpen :
                                   item.label === 'Aktivitas / Log' ? setAktivitasLogOpen : null;
                
                return (
                  <div key={item.path || item.label}>
                    <button
                      onClick={() => toggleOpen && toggleOpen(!isOpen)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 ${
                        isAnySubmenuActive
                          ? 'text-white shadow-lg'
                          : 'hover:bg-black/10 dark:hover:bg-white/10'
                      }`}
                      style={isAnySubmenuActive ? {
                        background: activeMenuBg,
                        color: activeMenuText
                      } : {}}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon className={`w-4 h-4 ${isAnySubmenuActive ? "text-white" : getIconColor(item.label)}`} />
                        <span className="text-xs font-medium">{item.label}</span>
                        {/* Show badge on parent when submenu is collapsed */}
                        {!isOpen && item.label === 'Pelanggan' && pendingRegistrations > 0 && (
                          <span
                            className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[9px] font-bold bg-red-500 text-white animate-pulse"
                            data-testid="badge-pending-registrations-parent"
                            title={`${pendingRegistrations} pendaftaran baru menunggu`}
                          >
                            {pendingRegistrations > 99 ? '99+' : pendingRegistrations}
                          </span>
                        )}
                      </div>
                      {isOpen ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronRight className="w-3 h-3" />
                      )}
                    </button>
                    
                    {/* Submenu */}
                    {isOpen && (
                      <div className="ml-6 mt-0.5 space-y-0.5">
                        {item.submenu.filter(sub => hasPermission(sub.roles)).map((subitem) => {
                          const isActive = location.pathname === subitem.path;
                          return (
                            <Link
                              key={subitem.path}
                              to={subitem.path}
                              onClick={() => setSidebarOpen(false)}
                              className={`${
                                isActive
                                  ? 'text-white shadow-md'
                                  : 'hover:bg-black/10 dark:hover:bg-white/10'
                              } flex items-center justify-between space-x-2 px-3 py-1.5 rounded-lg transition-all duration-200`}
                              style={isActive ? {
                                background: activeMenuBg,
                                color: activeMenuText
                              } : {}}
                            >
                              <span className="text-xs font-medium">{subitem.label}</span>
                              {subitem.path === '/app/registrations' && pendingRegistrations > 0 && (
                                <span
                                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                                    isActive ? 'bg-white text-indigo-700' : 'bg-red-500 text-white animate-pulse'
                                  }`}
                                  data-testid="badge-pending-registrations"
                                  title={`${pendingRegistrations} pendaftaran baru menunggu`}
                                >
                                  {pendingRegistrations > 99 ? '99+' : pendingRegistrations}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // External link item (like Portal Pelanggan or phpMyAdmin)
              if (item.isExternal) {
                if (item.externalUrl) {
                  return (
                    <a
                      key={item.label}
                      href={item.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setSidebarOpen(false)}
                      className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200"
                    >
                      <Icon className={`w-4 h-4 ${getIconColor(item.label)}`} />
                      <span className="text-xs font-medium">{item.label}</span>
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </a>
                  );
                }
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      if (item.onClick === 'openCustomerPortal') {
                        openCustomerPortal();
                      }
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200"
                  >
                    <Icon className={`w-4 h-4 ${getIconColor(item.label)}`} />
                    <span className="text-xs font-medium">{item.label}</span>
                    <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                  </button>
                );
              }
              
              // Regular menu item
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`${
                    isActive
                      ? 'text-white shadow-lg'
                      : 'hover:bg-black/10 dark:hover:bg-white/10'
                  } flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200`}
                  style={isActive ? {
                    background: activeMenuBg,
                    color: activeMenuText
                  } : {}}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-white" : getIconColor(item.label)}`} />
                  <span className="text-xs font-medium">{item.label}</span>
                </Link>
              );
            })}

            {/* Portal Pelanggan Menu - SuperAdmin Only */}
            {user?.role === 'super_admin' && tenantsList.length > 0 && (
              <div>
                <button
                  onClick={() => setPortalOpen(!portalOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200"
                >
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <span className="text-xs font-medium">Portal Pelanggan</span>
                  </div>
                  {portalOpen ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
                
                {/* Submenu - List of Tenants */}
                {portalOpen && (
                  <div className="ml-6 mt-0.5 space-y-0.5 max-h-60 overflow-y-auto">
                    {tenantsList.map((tenant) => (
                      <button
                        key={tenant.id}
                        onClick={() => {
                          openTenantPortal(tenant.tenant_code);
                          setSidebarOpen(false);
                        }}
                        className="w-full flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200 text-left"
                      >
                        <span className="text-xs font-medium truncate flex-1">{tenant.tenant_name}</span>
                        <ExternalLink className="w-3 h-3 opacity-50 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Orphan code removed - all menu items now rendered in main navItems loop above */}

            {/* Pengaturan Menu Group */}
            {settingsItems.length > 0 && (
              <div>
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200"
                >
                  <div className="flex items-center space-x-2">
                    <SettingsIcon className="w-4 h-4" />
                    <span className="text-xs font-medium">{t('settings')}</span>
                  </div>
                  {settingsOpen ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>
                
                {/* Submenu */}
                {settingsOpen && (
                  <div className="ml-6 mt-0.5 space-y-0.5">
                    {settingsItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`${
                            isActive
                              ? 'text-white shadow-md'
                              : 'hover:bg-black/10 dark:hover:bg-white/10'
                          } flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all duration-200`}
                          style={isActive ? {
                            background: `linear-gradient(to right, ${branding.primary_color || '#4F46E5'}, ${branding.secondary_color || '#10B981'})`
                          } : {}}
                        >
                          <Icon className="w-3 h-3" />
                          <span className="text-xs font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Aktivitas / Log Menu Group — placed after Pengaturan */}
            {aktivitasLogItems.length > 0 && (
              <div>
                <button
                  onClick={() => setAktivitasLogOpen(!aktivitasLogOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-all duration-200"
                >
                  <div className="flex items-center space-x-2">
                    <History className="w-4 h-4" />
                    <span className="text-xs font-medium">Aktivitas / Log</span>
                  </div>
                  {aktivitasLogOpen ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                </button>

                {aktivitasLogOpen && (
                  <div className="ml-6 mt-0.5 space-y-0.5">
                    {aktivitasLogItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`${
                            isActive
                              ? 'text-white shadow-md'
                              : 'hover:bg-black/10 dark:hover:bg-white/10'
                          } flex items-center space-x-2 px-3 py-1.5 rounded-lg transition-all duration-200`}
                          style={isActive ? {
                            background: `linear-gradient(to right, ${branding.primary_color || '#4F46E5'}, ${branding.secondary_color || '#10B981'})`
                          } : {}}
                        >
                          <Icon className="w-3 h-3" />
                          <span className="text-xs font-medium">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Footer - Fixed at bottom */}
          <div
            className="flex-shrink-0 p-2 max-h-[180px] overflow-y-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', borderTop: '1px solid color-mix(in srgb, currentColor 10%, transparent)' }}
          >
            <div className="text-center pt-1" style={{ color: 'currentColor' }}>
              <p className="text-[9px] font-semibold" style={{ opacity: 0.95 }}>
                {user?.role === 'super_admin' ? 'RADIUSLINK' : (branding.company_name || '')}
              </p>
              {branding.address && (
                <p className="text-[8px] mt-0.5 line-clamp-2" style={{ opacity: 0.7 }}>{branding.address}</p>
              )}
              {branding.phone && (
                <p className="text-[9px] mt-0.5" style={{ opacity: 0.9 }}>📞 {branding.phone}</p>
              )}
              {branding.email && (
                <p className="text-[9px]" style={{ opacity: 0.9 }}>📧 {branding.email}</p>
              )}
              <p className="text-[8px] mt-1" style={{ opacity: 0.55 }}>
                © 2025 {user?.role === 'super_admin' ? 'RADIUSLINK' : (branding.company_name || '')}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header 
          className="shadow-sm overflow-visible"
          style={{ ...getHeaderStyle(), borderBottom: '1px solid color-mix(in srgb, currentColor 12%, transparent)', '--rl-header-text': branding.header_text_color || '#ffffff' }}
        >
          <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5">
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden flex-shrink-0 p-1 opacity-80 hover:opacity-100"
                style={{ color: branding.header_text_color || '#ffffff' }}
                aria-label="Buka menu"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2
                className="text-base sm:text-lg font-bold truncate drop-shadow-sm tracking-wide"
                style={{ color: branding.header_text_color || '#ffffff' }}
              >
                {navItems.find(item => item.path === location.pathname)?.label || t('dashboard')}
              </h2>
            </div>
            
            <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0 ml-2">
              {/* Branch Selector - Hidden on very small screens, restricted for owner/kepala_cabang/teknisi */}
              {(() => {
                const userRoleLower = user?.role?.toLowerCase();
                const isRestrictedRole = userRoleLower === 'owner' || userRoleLower === 'kepala_cabang' || userRoleLower === 'customer_service' || userRoleLower === 'teknisi';
                const userBranch = branches.find(b => String(b.id) === String(user?.branch_id));
                
                // For restricted roles (owner/kepala_cabang/teknisi/customer service) - show only their branch (read-only)
                if (isRestrictedRole && user?.branch_id) {
                  // Use branch_name from user object first, then fallback to branches array
                  const branchName = user?.branch_name || userBranch?.name || `Cabang ${user.branch_id}`;
                  return (
                    <div className="hidden sm:flex items-center px-2 sm:px-3 py-1.5 bg-[color-mix(in_srgb,currentColor_12%,transparent)] border border-[color-mix(in_srgb,currentColor_25%,transparent)] rounded-lg text-xs sm:text-sm max-w-[120px] sm:max-w-none" style={{ color: branding.header_text_color || '#ffffff' }}>
                      <span>{branchName}</span>
                    </div>
                  );
                } else if (isRestrictedRole) {
                  // Restricted role but no branch assigned - show nothing or placeholder
                  return null;
                } else {
                  // For admin - show all branches dropdown
                  const isBranchActive = selectedBranch && selectedBranch !== 'all';
                  const activeBranchName = branches.find(b => b.id.toString() === selectedBranch?.toString())?.name;
                  return (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          style={isBranchActive ? undefined : { color: branding.header_text_color || '#ffffff' }}
                          className={`flex px-2 sm:px-3 py-1.5 h-8 rounded-lg text-[10px] sm:text-sm max-w-[180px] sm:max-w-none justify-between gap-1 border transition-all ${
                            isBranchActive
                              ? 'bg-orange-500 border-orange-300 text-white hover:bg-orange-600 shadow-lg shadow-orange-500/40 animate-[pulseBranch_2.5s_ease-in-out_infinite] focus:ring-orange-300'
                              : 'bg-[color-mix(in_srgb,currentColor_12%,transparent)] border-[color-mix(in_srgb,currentColor_25%,transparent)] hover:bg-[color-mix(in_srgb,currentColor_18%,transparent)] focus:ring-[color-mix(in_srgb,currentColor_50%,transparent)]'
                          } focus:outline-none focus:ring-2`}
                          data-testid="branch-selector"
                          data-branch-active={isBranchActive ? 'true' : 'false'}
                          title={isBranchActive ? `Mode Cabang Aktif: ${activeBranchName}. Klik "Semua Cabang" untuk kembali ke view global.` : undefined}
                        >
                          {isBranchActive && <Eye className="w-3.5 h-3.5 flex-shrink-0" />}
                          <span className="truncate font-semibold">
                            {isBranchActive
                              ? (activeBranchName || 'Cabang')
                              : (language === 'en' ? 'All Branches' : 'Semua Cabang')
                            }
                          </span>
                          <ChevronDown className="w-3 h-3 opacity-70 flex-shrink-0" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="rounded-xl max-h-[300px] overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
                        {isBranchActive && (
                          <div className="px-3 py-2 text-[11px] bg-orange-50 text-orange-700 border-b border-orange-100 flex items-center gap-1.5">
                            <Eye className="w-3 h-3" />
                            <span className="font-medium">Mode Cabang Aktif</span>
                          </div>
                        )}
                        <DropdownMenuItem 
                          onClick={() => setSelectedBranch('all')}
                          className={selectedBranch === 'all' ? 'bg-indigo-100' : ''}
                        >
                          {language === 'en' ? 'All Branches' : 'Semua Cabang'}
                        </DropdownMenuItem>
                        {branches.map(branch => (
                          <DropdownMenuItem 
                            key={branch.id} 
                            onClick={() => setSelectedBranch(String(branch.id))}
                            className={selectedBranch?.toString() === branch.id.toString() ? 'bg-indigo-100' : ''}
                          >
                            {branch.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
              })()}

              {/* ====== Router Selector (cascading by selected branch) ====== */}
              {(() => {
                const userRoleLower = user?.role?.toLowerCase();
                // 2026-05-28: Owner role NOW also gets router selector — useful
                // when owner has multiple routers (e.g. TONI RB5009 + TONI X86)
                // and wants to count PPPoE per router. Still hidden for
                // teknisi/CS who work at a more granular customer level.
                // 2026-06-02: teknisi (ID000029 KPO DEPOK) requested router selector to filter view
                const hideForRoles = ['customer_service','customer_service'];
                if (hideForRoles.includes(userRoleLower)) return null;
                // Cascading: filter routers by selectedBranch if a branch is selected.
                const visibleRouters = (selectedBranch && selectedBranch !== 'all')
                  ? routers.filter(r => String(r.branch_id || '') === String(selectedBranch))
                  : routers;
                const isRouterActive = selectedRouter && selectedRouter !== 'all';
                const activeRouter = routers.find(r => String(r.id) === String(selectedRouter));
                const activeRouterName = activeRouter?.name || activeRouter?.router_name || ('Router #' + String(selectedRouter));
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        style={isRouterActive ? undefined : { color: branding.header_text_color || '#ffffff' }}
                        className={'flex px-1.5 sm:px-3 py-1.5 h-8 w-8 sm:w-auto rounded-lg text-[10px] sm:text-sm sm:max-w-[180px] md:max-w-none justify-center sm:justify-between gap-1 border transition-all focus:outline-none focus:ring-2 ' + (isRouterActive ? 'bg-emerald-500 border-emerald-300 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/40 focus:ring-emerald-300' : 'bg-[color-mix(in_srgb,currentColor_12%,transparent)] border-[color-mix(in_srgb,currentColor_25%,transparent)] hover:bg-[color-mix(in_srgb,currentColor_18%,transparent)] focus:ring-[color-mix(in_srgb,currentColor_50%,transparent)]')}
                        data-testid="router-selector"
                        data-router-active={isRouterActive ? 'true' : 'false'}
                        title={isRouterActive ? ('Mode Router Aktif: ' + activeRouterName + '. Klik Semua Router untuk reset.') : 'Filter dashboard per-router'}
                      >
                        <Server className="w-4 h-4 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                        <span className="hidden sm:inline truncate font-semibold">
                          {isRouterActive
                            ? activeRouterName
                            : (language === 'en' ? 'All Routers' : 'Semua Router')
                          }
                        </span>
                        <ChevronDown className="hidden sm:inline-block w-3 h-3 opacity-70 flex-shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="rounded-xl max-h-[320px] overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]">
                      {isRouterActive && (
                        <div className="px-3 py-2 text-[11px] bg-emerald-50 text-emerald-700 border-b border-emerald-100 flex items-center gap-1.5">
                          <Server className="w-3 h-3" />
                          <span className="font-medium">Mode Router Aktif</span>
                        </div>
                      )}
                      <DropdownMenuItem
                        onClick={() => setSelectedRouter('all')}
                        className={selectedRouter === 'all' ? 'bg-indigo-100' : ''}
                        data-testid="router-option-all"
                      >
                        {language === 'en' ? 'All Routers' : 'Semua Router'}
                        <span className="ml-auto text-[10px] text-gray-500">{visibleRouters.length}</span>
                      </DropdownMenuItem>
                      {visibleRouters.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500 italic">Tidak ada router di cabang ini</div>
                      ) : (
                        visibleRouters.map(r => {
                          const pppoeTotal = r.customer_pppoe_total ?? r.customer_total ?? 0;
                          const isolir = r.customer_pppoe_isolir ?? 0;
                          return (
                            <DropdownMenuItem
                              key={r.id}
                              onClick={() => setSelectedRouter(String(r.id))}
                              className={String(selectedRouter) === String(r.id) ? 'bg-indigo-100' : ''}
                              data-testid={'router-option-' + String(r.id)}
                            >
                              <div className="flex flex-col flex-1">
                                <span className="font-medium flex items-center gap-1.5">
                                  <span className={'inline-block w-1.5 h-1.5 rounded-full ' + (r.is_online ? 'bg-emerald-500' : 'bg-gray-300')}></span>
                                  {r.name || r.router_name || ('Router #' + String(r.id))}
                                </span>
                                <span className="text-[10px] text-gray-500">{r.host || r.ip_address || ''}</span>
                              </div>
                              <div className="flex flex-col items-end ml-3">
                                <span className="text-[11px] font-semibold text-indigo-700">{pppoeTotal} PPPoE</span>
                                {isolir > 0 && (
                                  <span className="text-[10px] text-amber-600">{isolir} isolir</span>
                                )}
                              </div>
                            </DropdownMenuItem>
                          );
                        })
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}

              {/* Language & Theme toggles moved to user dropdown */}

              {/* 2026-02-04: BrandLogo dipindah ke main content (lihat <main> di bawah) */}

              {/* Notification Bell - Overdue Tickets */}
              {hasPermission(['admin', 'kepala_cabang', 'customer_service', 'super_admin']) && (
                <div className="relative" data-notification-dropdown>
                  <button
                    onClick={() => setNotificationOpen(!notificationOpen)}
                    className="relative flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-white/95 hover:bg-white shadow-md ring-1 ring-red-200 transition-colors"
                    style={{ color: '#dc2626' }}
                    title={`${overdueCount} Tiket Overdue`}
                    data-testid="notification-bell"
                  >
                    <Bell className="w-4 h-4" strokeWidth={2.5} />
                    {overdueCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white bg-red-500 rounded-full px-1 animate-pulse">
                        {overdueCount > 99 ? '99+' : overdueCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {notificationOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
                      {/* Header */}
                      <div className="px-4 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="font-semibold text-sm">Tiket Overdue</span>
                          </div>
                          <span className="text-xs bg-[color-mix(in_srgb,currentColor_12%,transparent)] px-2 py-0.5 rounded-full">
                            {overdueCount} tiket
                          </span>
                        </div>
                        <p className="text-xs text-white/80 mt-1">
                          Tiket belum selesai lebih dari 24 jam
                        </p>
                      </div>

                      {/* Ticket List */}
                      <div className="max-h-80 overflow-y-auto">
                        {overdueTickets.length === 0 ? (
                          <div className="p-6 text-center">
                            <div className="w-12 h-12 mx-auto mb-3 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                              <Ticket className="w-6 h-6 text-green-600 dark:text-green-400" />
                            </div>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Tidak ada tiket overdue</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Semua tiket ditangani tepat waktu</p>
                          </div>
                        ) : (
                          overdueTickets.slice(0, 5).map((ticket) => (
                            <div
                              key={ticket.id}
                              className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                              onClick={() => {
                                navigate(`/app/tickets/${ticket.id}`);
                                setNotificationOpen(false);
                              }}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                                      #{ticket.ticket_number}
                                    </span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                      ticket.priority === 'URGENT' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                      ticket.priority === 'HIGH' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    }`}>
                                      {ticket.priority}
                                    </span>
                                  </div>
                                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate mt-1">
                                    {ticket.customer_name}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {ticket.ticket_type} • {ticket.assigned_to_name || 'Belum di-assign'}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                                    <Clock className="w-3 h-3" />
                                    <span className="text-xs font-medium">
                                      {ticket.overdue_hours > 48 
                                        ? `${Math.floor(ticket.overdue_hours / 24)}d` 
                                        : `${Math.round(ticket.overdue_hours)}h`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Footer */}
                      {overdueTickets.length > 0 && (
                        <div className="p-3 bg-slate-50 dark:bg-slate-700/50 border-t border-slate-200 dark:border-slate-600">
                          <button
                            onClick={() => {
                              navigate('/app/tickets');
                              setNotificationOpen(false);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium hover:opacity-80 rounded-lg transition-colors"
                            style={{ color: branding.primary_color || '#4F46E5' }}
                          >
                            <Eye className="w-4 h-4" />
                            Lihat Semua Tiket
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center p-0.5 sm:p-1.5 rounded-lg hover:bg-[color-mix(in_srgb,currentColor_12%,transparent)] transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div 
                    className="w-7 h-7 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold overflow-hidden flex-shrink-0 border-2 border-white/50"
                    style={{
                      background: `linear-gradient(to bottom right, ${branding.primary_color || '#4F46E5'}, ${branding.secondary_color || '#10B981'})`
                    }}
                  >
                    {user?.avatar_url ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL || ''}${user.avatar_url}?t=${user.updated_at || user.last_login || '1'}`} 
                        alt={user.full_name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      user?.full_name?.charAt(0).toUpperCase() || 'U'
                    )}
                  </div>
                  <ChevronDown className="w-3 h-3 text-white hidden sm:block" />
                </button>

                {/* Dropdown Menu - Fixed for mobile */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-1 w-44 sm:w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 animate-slide-down">
                    <div className="p-2 sm:p-3 border-b border-slate-200 dark:border-slate-700">
                      <div className="font-semibold text-xs text-slate-800 dark:text-white truncate">{user?.full_name}</div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">@{user?.username}</div>
                      <div className="mt-1">
                        <span 
                          className="inline-block px-1.5 py-0.5 text-[9px] rounded-full"
                          style={{
                            backgroundColor: `${branding.primary_color || '#4F46E5'}20`,
                            color: branding.primary_color || '#4F46E5'
                          }}
                        >
                          {user?.role?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="p-1 sm:p-1.5">
                      <Link
                        to="/app/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <UserCircle className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        <span className="text-xs text-slate-700 dark:text-slate-200">Profile</span>
                      </Link>
                      {/* Theme Dashboard submenu */}
                      <div className="px-2 sm:px-3 pt-2 pb-1">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Tema Dashboard</div>
                      </div>
                      {[
                        { id: 'glass',      label: 'Glassmorphism',    icon: Sparkles,   color: '#8b5cf6' },
                        { id: 'light',      label: 'Clean Minimalist', icon: Sun,        color: '#3b82f6' },
                        { id: 'dark',       label: 'Dark Premium',     icon: Moon,       color: '#60a5fa' },
                        { id: 'paper-white', label: 'Paper White',    icon: Sun,        color: '#0f172a' },
                      ].map(t => {
                        const TIcon = t.icon;
                        const active = dashboardTheme.theme === t.id && !dashboardTheme.autoDayNight;
                        return (
                          <button
                            key={t.id}
                            data-testid={`menu-theme-${t.id}`}
                            onClick={() => {
                              if (dashboardTheme.autoDayNight) dashboardTheme.setAutoDayNight(false);
                              dashboardTheme.setTheme(t.id);
                              setUserMenuOpen(false);
                            }}
                            className="w-full flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          >
                            <TIcon className="w-4 h-4" style={{ color: t.color }} />
                            <span className={`text-xs ${active ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-200'}`}>{t.label}</span>
                            {active && <span className="ml-auto text-[9px]" style={{ color: t.color }}>●</span>}
                          </button>
                        );
                      })}
                      {/* Auto Day/Night toggle */}
                      <div className="px-2 sm:px-3 pt-2 pb-1">
                        <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Auto Day/Night</div>
                      </div>
                      <button
                        data-testid="menu-auto-daynight-toggle"
                        onClick={() => dashboardTheme.setAutoDayNight(!dashboardTheme.autoDayNight)}
                        className="w-full flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span className="text-xs text-slate-700 dark:text-slate-200 flex-1 text-left">Otomatis ikut jam</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${dashboardTheme.autoDayNight ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                          {dashboardTheme.autoDayNight ? 'ON' : 'OFF'}
                        </span>
                      </button>

                      <div className="border-t border-slate-200 dark:border-slate-700 my-1"></div>
                      {/* Language toggle */}
                      <button
                        onClick={() => { toggleLanguage(); setUserMenuOpen(false); }}
                        data-testid="menu-language-toggle"
                        className="w-full flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Globe className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        <span className="text-xs text-slate-700 dark:text-slate-200">Bahasa: <strong>{language === 'en' ? 'English' : 'Indonesia'}</strong></span>
                      </button>
                      {/* Dark/Light theme toggle */}
                      <button
                        onClick={() => { toggleTheme(); setUserMenuOpen(false); }}
                        data-testid="menu-darklight-toggle"
                        className="w-full flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                      >
                        {theme === 'light'
                          ? <Moon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          : <Sun className="w-4 h-4 text-slate-600 dark:text-slate-300" />}
                        <span className="text-xs text-slate-700 dark:text-slate-200">Mode: <strong>{theme === 'light' ? 'Terang' : 'Gelap'}</strong></span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-xs">Logout</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Subscription Banner */}
        <SubscriptionBanner />
        
        {/* Announcement Banner from Superadmin */}
        <AnnouncementBanner />
        
        {/* Page Content */}
        <main className="flex-1 overflow-y-auto scrollbar-hide" style={{...getContentBgStyle(), scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 2026-02: Global Voice Command (Web Speech API). Aktif untuk semua role yang login. */}
      <VoiceCommandListener />
    </div>
    </>
  );
};

export default Layout;